import { defineStore } from 'pinia'
import { ref } from 'vue'
import type { InvitationPayload } from '@/store/types/InvitationPayload'
import type { ParsedError } from '@/backend/error/types'
import type { MembershipRole } from '@/store/types/MembershipRole'
import {
  addInvitations,
  deleteInvitation,
  getPendingInvitations,
  updateInvitation,
} from '@/modules/Teams/invitesCRUD'
import { useToast } from '@/uiKit/Toast/useToast'

export const useInviteStore = defineStore('inviteStore', () => {
  const toast = useToast()

  const invitations = ref<InvitationPayload[]>([])

  const getUnconfirmedInvitations = async (
    teamSlug: string,
  ): Promise<InvitationPayload[] | ParsedError> => {
    const response = await getPendingInvitations(teamSlug)

    if (!('error' in response)) {
      invitations.value = response
    }

    return response
  }

  type CreateInvitationResponse = {
    email?: string
    role?: string
    success: boolean
  }
  const createInvitations = async (
    teamSlug: string,
    invitationsPayload: { email: string; role: MembershipRole }[],
  ): Promise<CreateInvitationResponse> => {
    const response = await addInvitations(teamSlug, invitationsPayload)

    if ('error' in response) {
      if ('isValidationError' in response.error && response.error.isValidationError) {
        const errors: CreateInvitationResponse = { success: false }

        if (typeof response.error.email === 'string') {
          errors.email = response.error.email
        }

        if (typeof response.error.role === 'string') {
          errors.role = response.error.role
        }

        return errors
      }

      const message =
        typeof response.error.message === 'string' ? response.error.message : 'Something went wrong'

      toast.warning({ meta: { title: message } })
      return { success: false }
    }

    invitations.value = invitations.value.concat(response)
    toast.success({ meta: { title: 'Invitation successfully sent' } })
    return { success: true }
  }

  const updateInvitationRole = async (
    invitationId: number,
    newRole: MembershipRole,
  ): Promise<void> => {
    const response = await updateInvitation(invitationId, newRole)

    if ('error' in response) {
      const message =
        typeof response.error.message === 'string'
          ? response.error.message
          : 'Something went wrong while updating invitation role'

      toast.warning({ meta: { title: message } })
    }

    invitations.value = invitations.value.map((invitation) => {
      if (invitation.id === invitationId) {
        return { ...invitation, role: newRole }
      }
      return invitation
    })
  }

  const deleteInvitationById = async (
    teamId: number,
    invitationId: InvitationPayload['id'],
  ): Promise<void> => {
    const response = await deleteInvitation(teamId, invitationId)
    if ('error' in response) {
      const message =
        typeof response.error.message === 'string'
          ? response.error.message
          : 'Something went wrong while deleting invitation'
      toast.warning({ meta: { title: message } })
      return
    }

    invitations.value = invitations.value.filter((invitation) => invitation.id !== invitationId)
    toast.success({ meta: { title: 'Invitation successfully revoked' } })
  }

  return {
    invitations,
    getUnconfirmedInvitations,
    createInvitations,
    deleteInvitationById,
    updateInvitationRole,
  }
})
